import {
  FileText,
  Handshake,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Receipt,
  User,
  Users
} from "lucide-react";
import { Button } from "primereact/button";
import { ReactNode, useEffect, useState } from "react";
import { ContasPagarComponent } from "../components/ContasPagarComponent";
import MensagemAlertaDialog from "../components/dialog/MensagemAlertaDialog";
import { ExtratoComponent } from "../components/ExtratoComponent";
import UsuariosComponent from "../components/UsuariosComponent";
import CorretoresComponent from "../components/CorretoresComponent";
import EmailsComissaoEnviadosComponent from "../components/EmailsComissaoEnviadosComponent";
import BuscaDetalhada from "../model/BuscaDetalhada";
import Usuario from "../model/Usuario";
import { useLoadingContext } from "../provider/LoadingProvider";
import AuthService from "../service/AuthService";
import ExtratoService from "../service/financeiro/ExtratoService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface DashboardProps {
  onLogout: () => void;
}

type View = "contas" | "extrato" | "usuarios" | "corretores" | "emailsComissao" | "";

const authService = new AuthService();
const extratoService = new ExtratoService();
export function Dashboard({ onLogout }: DashboardProps) {
  const [currentView, setCurrentView] = useState<View>("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const { startLoading, stopLoading } = useLoadingContext();

  const [dialogMensagem, setDialogMensagem] = useState<{ visible: boolean, mensagem: string | ReactNode, titulo: string, whatsapp?: "" }>({ visible: false, mensagem: "", titulo: "", whatsapp: undefined });
  const [visibleMensagemIptu, setVisibleMensagemIptu] = useState<boolean>(false);

  const [buscaDetalhada, setBuscaDetalhada] = useState<BuscaDetalhada>(new BuscaDetalhada([], []));

  useEffect(() => {
    alertarIptuSeNecessario();
    buscarDadosDoUsuario();
    registrarEventoAoAlterarToken();
  }, []);

  function registrarEventoAoAlterarToken() {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "token") {
        window.location.reload();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }

  function alertarIptuSeNecessario() {
    if (new Date().getMonth() < 7) {
      setVisibleMensagemIptu(true);
    }
  }

  function buscarDadosDoUsuario() {
    authService.getDadosUsuarioLogado().then((data) => {
      emitirMensagens(data);
      setUsuario(data);
    }).catch((err) => {
      console.log(err);
    });
  }

  function emitirMensagens(data: Usuario) {
    if (data.subjudice) {
      setDialogMensagem({
        visible: true,
        mensagem: "Para mais informações, solicitamos que entre em contato com o setor financeiro.",
        titulo: "Sua conta consta com status sub judice."
      });
    } else if (data.defaulting) {
      setDialogMensagem({
        visible: true,
        mensagem: <span style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <span>
            Por gentileza, entre em contato com o setor financeiro para regularizar e evitar o envio a cobrança jurídica.
          </span>
          <span>
            <a href={`https://wa.me/48998210480`} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: "15px", color: "#25D366", fontWeight: "bold" }}>
              <FontAwesomeIcon icon={["fab", "whatsapp"]} /> (48) 99821-0480
            </a>
          </span>
        </span>,
        titulo: "IDENTIFICAMOS PENDÊNCIAS FINANCEIRAS EM SEU CONTRATO!"
      });
    }
  }

  useEffect(() => {
    load();
  }, [usuario])

  const load = () => {
    if (usuario && usuario.idClienteSienge) {
      startLoading();
      extratoService.pesquisar().then((data) => {
        stopLoading();
        data.itensExtrato.sort((a, b) => b.dataPagamento > a.dataPagamento ? 1 : -1);
        data.contasPagar.sort((a, b) => b.vencimento < a.vencimento ? 1 : -1);
        setBuscaDetalhada(data);
      }).catch((err) => {
        stopLoading();
        console.log(err);
      });
    }
  }

  const menuItems = [
    {
      id: "contas" as View,
      label: "Contas a Pagar",
      icon: <FileText size={18} />,
      visible: (
        usuario?.idClienteSienge !== null && usuario?.idClienteSienge !== undefined
        && buscaDetalhada != null && buscaDetalhada.contasPagar.length > 0
      )
    },
    {
      id: "extrato" as View,
      label: "Extrato",
      icon: <Receipt size={18} />,
      visible: (
        usuario?.idClienteSienge !== null && usuario?.idClienteSienge !== undefined
        && buscaDetalhada != null && buscaDetalhada.itensExtrato.length > 0
      ),
    },
    {
      id: "usuarios" as View,
      label: "Usuários",
      icon: <Users size={18} />,
      visible: usuario?.admin || false,
    },
    {
      id: "corretores" as View,
      label: "Corretores",
      icon: <Handshake size={18} />,
      visible: usuario?.admin || false,
    },
    {
      id: "emailsComissao" as View,
      label: "E-mails enviados",
      icon: <Mail size={18} />,
      visible: usuario?.admin || false,
    },
  ];

  const handleMenuClick = (view: View) => {
    setCurrentView(view);
    setMobileMenuOpen(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8f9fa" }}>
      {dialogMensagem ? <MensagemAlertaDialog
        visible={dialogMensagem.visible}
        onHide={() => setDialogMensagem({ visible: false, mensagem: "", titulo: "" })}
        mensagem={dialogMensagem.mensagem}
        titulo={dialogMensagem.titulo}
      /> : null}

      {visibleMensagemIptu ? <MensagemAlertaDialog
        visible={visibleMensagemIptu}
        onHide={() => setVisibleMensagemIptu(false)}
        mensagem={<span style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <span>
            O carnê do IPTU deve ser acessado no site da Prefeitura do município onde fica o seu loteamento.
          </span>
          <span>
            O número do cadastro municipal está no seu contrato, no Quadro Completo – Cláusula Segunda.
          </span>
          <span>
            Ficou com dúvida? Fale com o nosso comercial.
            <a href={`https://wa.me/48996290118`} target="_blank" rel="noopener noreferrer" style={{ display: "block", marginTop: "15px", color: "#25D366", fontWeight: "bold" }}>
              <FontAwesomeIcon icon={["fab", "whatsapp"]} /> (48) 99629-0118
            </a>
          </span>
        </span>}
        titulo={"ATENÇÃO AO IPTU 2026!"}
      /> : null}


      {/* Header */}
      <div className="layout-topbar">
        <div className="logo-container">
          <Button
            icon={<Menu size={20} color="white" />}
            className="p-button-text p-button-rounded md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
          <div className="logo-icon hidden md:flex">
            <LayoutDashboard size={20} />
          </div>
          <span className="hidden md:inline" style={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Portal do Cliente
          </span>
        </div>

        <div className="flex align-items-center gap-3">
          <div className="user-details flex align-items-center gap-2">
            <div
              style={{
                width: "36px",
                height: "36px",
                background: "#e5e7eb",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <User size={18} />
            </div>
            <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2" }}>
              <small style={{ fontWeight: 500 }}>Bem-vindo, {authService.getTokenData().nome.split(" ")[0]}!</small>
              <small className="text-muted">{authService.getTokenData().email}</small>
            </div>
          </div>
          <Button
            icon={<LogOut size={16} />}
            label="Sair"
            className="p-button-outlined p-button-sm"
            onClick={() => {
              localStorage.removeItem('token');
              onLogout();
            }}
          />
        </div>
      </div>

      <div className="grid">
        {/* Sidebar */}
        <aside
          className={`col-12 md:col-3 lg:col-2 layout-sidebar ${mobileMenuOpen ? "active" : ""
            }`}
        >
          <nav>
            {menuItems.map((item) => {
              const isActive = currentView === item.id;
              return (
                item.visible ? <div key={item.id} className="menu-item">
                  <Button
                    label={item.label}
                    icon={item.icon}
                    className={isActive ? "active" : ""}
                    onClick={() => handleMenuClick(item.id)}
                  />
                </div> : <></>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="col-12 md:col-9 lg:col-10 layout-main">
          {currentView === "contas" && <ContasPagarComponent itens={buscaDetalhada.contasPagar} />}
          {currentView === "extrato" && <ExtratoComponent itens={buscaDetalhada.itensExtrato} />}
          {currentView === "usuarios" && <UsuariosComponent />}
          {currentView === "corretores" && <CorretoresComponent />}
          {currentView === "emailsComissao" && <EmailsComissaoEnviadosComponent />}
        </main>
      </div>

      {/* Overlay para mobile menu */}
      {mobileMenuOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 998,
          }}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
}
